import React, { useState } from 'react';

/**
 * ImageWithFallback Component
 *
 * Issue #1404 - Professional image placeholder for broken/missing images
 * Issue #1396 - Loading states and retry logic for images
 *
 * Features:
 * - Loading skeleton with pulse animation
 * - Retry logic with exponential backoff (2 retries)
 * - Inline SVG placeholder fallback (no external file dependency)
 * - Memory leak prevention (cleanup timeouts)
 *
 * Benefits:
 * - Professional appearance even with broken images
 * - Better UX with loading states
 * - No broken image icons
 * - Consistent design across blog platform
 */

interface ImageWithFallbackProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  loading?: 'lazy' | 'eager';
  showPlaceholder?: boolean;
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  src,
  alt,
  className = 'w-full h-full object-cover relative z-10',
  containerClassName = 'aspect-video bg-gray-200 overflow-hidden relative',
  loading = 'lazy',
  showPlaceholder = true
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageLoaded(true);
    // Hide skeleton on successful load
    const skeleton = e.currentTarget.previousElementSibling as HTMLElement;
    if (skeleton) skeleton.style.display = 'none';
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const retryCount = parseInt(img.dataset.retryCount || '0', 10);
    const maxRetries = 2;

    // Clear any existing retry timeout to prevent memory leaks (Issue #1398)
    const imgWithTimeout = img as HTMLImageElement & { __retryTimeout?: NodeJS.Timeout };
    const existingTimeout = imgWithTimeout.__retryTimeout;
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      delete imgWithTimeout.__retryTimeout;
    }

    if (retryCount < maxRetries) {
      // Retry with exponential backoff (Issue #1396)
      const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s
      img.dataset.retryCount = (retryCount + 1).toString();

      // Store timeout ID for cleanup
      imgWithTimeout.__retryTimeout = setTimeout(() => {
        const originalSrc = img.dataset.originalSrc || img.src;
        if (!img.dataset.originalSrc) {
          img.dataset.originalSrc = img.src;
        }
        // Force reload with cache-busting query param
        img.src = `${originalSrc}?retry=${retryCount + 1}`;
        delete imgWithTimeout.__retryTimeout;
      }, delay);
    } else {
      // Max retries reached, show placeholder (Issue #1404)
      setImageError(true);
      setImageLoaded(true);
      // Hide skeleton
      const skeleton = img.previousElementSibling as HTMLElement;
      if (skeleton) skeleton.style.display = 'none';
    }
  };

  // Inline SVG placeholder - no external file dependency
  const PlaceholderSVG = () => (
    <div className="aspect-video bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center">
      <svg
        className="w-16 h-16 text-teal-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    </div>
  );

  // Show placeholder if image failed and showPlaceholder is enabled
  if (imageError && showPlaceholder) {
    return (
      <div className={containerClassName}>
        <PlaceholderSVG />
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      {/* Loading skeleton with pulse animation */}
      {!imageLoaded && (
        <div
          className="absolute inset-0 bg-gray-200 animate-pulse"
          aria-hidden="true"
        />
      )}

      {/* Image with retry logic */}
      <img
        src={src}
        alt={alt}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError}
        className={className}
      />
    </div>
  );
};

export default ImageWithFallback;
